import Vue from 'vue';

const getClient = () => Vue.api.getClient('bookmarks');

export const STATUS = {
  LOADING: 'bookmarks.status.loading',
  SUCCESS: 'bookmarks.status.success',
  CANCELLED: 'bookmarks.status.cancelled',
  ERROR: 'bookmarks.status.error',
};

export const BOOKMARK_TYPES = {
  REQUESTS: 'requests',
  INCIDENTS: 'incidents',
};

const bookmarkItemInitialState = {
  bookmark: null,
  status: '',
  error: null,
};

const initialState = {
  [BOOKMARK_TYPES.REQUESTS]: JSON.parse(JSON.stringify(bookmarkItemInitialState)),
  [BOOKMARK_TYPES.INCIDENTS]: JSON.parse(JSON.stringify(bookmarkItemInitialState)),
  syncRequest: null,
};

const mutations = {
  setBookmark(state, { type, bookmark }) {
    state[type].bookmark = bookmark;
  },
  addBookmarkItem(state, { type, item: { element, name } }) {
    const { bookmark } = state[type];
    bookmark.addItem(element, name);
  },
  removeBookmarkItem(state, { type, item: { element } }) {
    const { bookmark } = state[type];
    bookmark.removeItem(element);
  },
  setStatus(state, { type, status }) {
    state[type].status = status;
  },
  setError(state, { type, error }) {
    state[type].error = error;
  },
  setSyncRequest(state, syncRequest) {
    state.syncRequest = syncRequest;
  },
  reset(state) {
    Object.values(BOOKMARK_TYPES)
      .forEach((key) => {
        state[key] = bookmarkItemInitialState;
      });
  },
};

const isValidType = (type) => Object.values(BOOKMARK_TYPES).includes(type);

const actions = {
  async get({ commit }, { type }) {
    if (!isValidType(type)) {
      throw new Error(`invalid bookmark type ${type}`);
    }
    try {
      commit('setStatus', { type, status: STATUS.LOADING });
      const result = await getClient().get(type);
      commit('setBookmark', { type, bookmark: result.unique() });
      commit('setStatus', { type, status: STATUS.SUCCESS });
    } catch (ex) {
      console.error(`error getting bookmark of type ${type}`, ex);
      commit('setError', { type, error: ex });
      commit('setStatus', { type, status: STATUS.ERROR });
      commit('setBookmark', null);
    }
  },
  async add({ commit, state, getters }, { type, element: { id, name } }) {
    if (type == null) {
      throw new Error('bookmark type is required');
    } else if (!isValidType(type)) {
      throw new Error(`invalid bookmark type ${type}`);
    }
    if (getters.isInBookmark(type, id)) {
      throw new Error(`element ${id} already exists in bookmark`);
    }

    const item = { element: `U'${id}'`, name };
    let req;
    try {
      const { bookmark } = state[type];

      if (state.syncRequest != null) {
        state.syncRequest.cancel();
      }
      commit('addBookmarkItem', { type, item });

      req = getClient().set(type, {
        bookmark: {
          [bookmark.type]: bookmark.items.map((bookmarkItem) => ({
            element: bookmarkItem.element,
            name: bookmarkItem.name,
          })),
        },
      });

      commit('setSyncRequest', req);
      commit('setStatus', { type, status: STATUS.LOADING });
      await req; // wait for request solving to change status
      commit('setSyncRequest', null);
      commit('setStatus', { type, status: STATUS.SUCCESS });
    } catch (ex) {
      console.error(`error setting bookmark of type ${type}`, ex, (req || {}).cancelled);
      if (req != null && !req.cancelled) {
        commit('removeBookmarkItem', { type, item });
        commit('setError', { type, error: ex });
        commit('setStatus', { type, status: STATUS.ERROR });
        throw ex;
      }
    }
  },
  async remove({ commit, state, getters }, { type, element: { id } }) {
    if (type == null) {
      throw new Error('bookmark type is required');
    } else if (!isValidType(type)) {
      throw new Error(`invalid bookmark type ${type}`);
    }
    if (!getters.isInBookmark(type, id)) {
      throw new Error(`element ${id} doesn't exists in bookmark`);
    }

    const item = { element: `U'${id}'` };
    let req;
    try {
      if (state.syncRequest != null) {
        state.syncRequest.cancel();
      }
      commit('removeBookmarkItem', { type, item });

      const { bookmark } = state[type];

      req = getClient().set(type, {
        bookmark: {
          [bookmark.type]: bookmark.items.map((bookmarkItem) => ({
            element: bookmarkItem.element,
            name: bookmarkItem.name,
          })),
        },
      });

      commit('setSyncRequest', req);
      commit('setStatus', { type, status: STATUS.LOADING });
      await req; // wait for request solving to change status
      commit('setSyncRequest', null);
      commit('setStatus', { type, status: STATUS.SUCCESS });
    } catch (ex) {
      console.error(`error setting bookmark of type ${type}`, ex, (req || {}).cancelled);
      if (req != null && !req.cancelled) {
        commit('addBookmarkItem', { type, item });
        commit('setError', { type, error: ex });
        commit('setStatus', { type, status: STATUS.ERROR });
        console.log('throwing ex');
        throw ex;
      }
    }
  },
  reset({ commit }) {
    commit('reset');
  },
};

const getters = {
  status: (state) => (type) => (state[type] || {}).status || null,
  list: (state) => (type) => (state[type].bookmark || {}).items || [],
  isInBookmark: (state) => function isInBookmark(type, element) {
    const list = (state[type].bookmark || {}).items || [];
    return list.some((bookmark) => bookmark.id === element);
  },
};

export default {
  namespaced: true,
  state: initialState,
  mutations,
  actions,
  getters,
};
